import { useState, useEffect, useMemo } from 'react';
import { ColumnType } from 'components/table-component/table-component.props';

interface CalculateRowsWidthResult {
  calculatedRowWidth: number;
  recalculatedCustomizedColumnsWidth: ColumnType[];
}

export const useRecalculateRowsAndColumnsWidth = (
  customizedColumns: ColumnType[],
  defaultColumns: ColumnType[],
  maxWidthInPercent: number
): CalculateRowsWidthResult => {
  const [calculatedRowWidth, setCalculatedRowWidth] = useState<number>(100);

  useEffect(() => {
    if (customizedColumns?.length && defaultColumns?.length) {
      const customizedColumnsLength = customizedColumns?.length ?? 0;
      const defaultColumnsLength = defaultColumns?.length ?? 0;
      const isEqually = customizedColumnsLength === defaultColumnsLength;

      if (isEqually) {
        return setCalculatedRowWidth(maxWidthInPercent);
      }
      if (customizedColumnsLength && defaultColumnsLength && maxWidthInPercent) {
        const newRowWidth = Math.ceil((maxWidthInPercent * customizedColumnsLength) / defaultColumnsLength);
        setCalculatedRowWidth(!!newRowWidth && newRowWidth > 100 ? newRowWidth : 100);
      }
    }
  }, [customizedColumns?.length]);

  const recalculatedCustomizedColumnsWidth = useMemo(() => {
    const customizedColumnsLength = customizedColumns?.length ?? 0;
    const isNonEqually = customizedColumns?.length !== defaultColumns?.length;

    if (customizedColumnsLength && isNonEqually && calculatedRowWidth === 100) {
      const columnsWithNonFixedWidth = customizedColumns.filter(column => !!column.name && column?.width.includes('%'));
      const widthPerColumn = (100 / columnsWithNonFixedWidth.length).toFixed(2);

      return columnsWithNonFixedWidth.length
        ? customizedColumns.map(column => {
            const searchedColumn = defaultColumns.find(defColumn => !!column.name && defColumn.name === column.name);
            if (searchedColumn && column?.width && column.width.includes('%')) {
              return {
                ...column,
                width: widthPerColumn + '%'
              };
            }
            return column;
          })
        : customizedColumns;
    }

    if (customizedColumnsLength && calculatedRowWidth > 100) {
      return customizedColumns.map(column => {
        const searchedColumn = defaultColumns.find(
          defColumn => !!column.name && defColumn.name === column.name && defColumn?.width
        );
        if (searchedColumn && searchedColumn?.width && searchedColumn.width.includes('%')) {
          return {
            ...column,
            width: searchedColumn.width
          };
        }
        return column;
      });
    }

    return customizedColumns;
  }, [calculatedRowWidth, [...customizedColumns.map(col => col.name)]]);

  return { calculatedRowWidth, recalculatedCustomizedColumnsWidth };
};
